import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { twMerge } from "tailwind-merge";
import toast from "react-hot-toast";
import { Search, SlidersHorizontal, User } from "lucide-react";
import {
  DeviceType,
  useGetCategoriesQuery,
  Visitor,
} from "@/graphql/generated/schema";
import { CMSModal } from "@/context";
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import Select from "@/designs/Select";
import Table from "@/designs/Table";
import Pagination from "@/designs/Pagination";
import DropDown from "@/designs/Dropdown";
import AddNewVisit from "./AddNewVisit";
import DateRangePicker from "@/designs/DateRangePicker";
import EditTimeline from "./EditTimeline";
import DeleteRow from "./DeleteRow";
import SignOutModal from "./SignOutModal";
import { STATUS_FILTERS } from "./types";
import { visitorColumns } from "./TimelineVisitorColumns";
import { useVisitorData } from "../hooks/useVisitorData";
import { useVisitorActions } from "../hooks/useVisitorAction";
import { useVisitorSelection } from "../hooks/useVisitorSelection";
import moment from "moment";
import { AnimatePresence, motion } from "framer-motion";
import OfficeLocation from "@/common/OfficeLocation";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "next/navigation";

const Timeline = () => {
  const { theme, selectedLocation } = useContext(CMSModal);
  const { t } = useTranslation();
  const search = useSearchParams();
  const employee = search.get("emp");
  const statusParam = search.get("status");
  const [addVisit, setAddVisit] = useState(false);
  const [editTimeline, setEditTimeline] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<Visitor | any>(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(!!statusParam);
  const [signOut, setSignOut] = useState(false);
  const [deleteRow, setDeleteRow] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 350);

  const [filters, setFilters] = useState({
    search: "",
    status: statusParam ?? "All",
    category: "",
    location: selectedLocation,
    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    sorted: {
      direction: "",
      columnId: "",
    },
  });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, search: debouncedSearch }));
  }, [debouncedSearch]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, location: selectedLocation }));
  }, [selectedLocation]);
  const {
    visitors,
    totalPages,
    loading,
    updateVisitor,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalCount,
  } = useVisitorData(filters);

  useEffect(() => {
    if (!employee) return;
    const visitorData = visitors?.find((v) => v?.id === employee);

    setSelectedVisit(visitorData);
    setEditTimeline(true);
  }, [employee, visitors]);
  const { data: categoriesData } = useGetCategoriesQuery({
    variables: {
      location: selectedLocation,
    },
  });
  const categories: any = [
    { label: "All", value: "" },
    ...(categoriesData?.getCategories?.map((c) => ({
      label: c?.name,
      value: c?.id,
    })) || []),
  ];

  const handleStatusChange = useCallback(
    async (id: string, newStatus: string) => {
      try {
        await updateVisitor({
          variables: {
            input: {
              _id: id,
              signedType: newStatus,
              signedOutDevice: DeviceType.Web,
            },
          },
        });
        toast.success("Status updated successfully");
      } catch {
        toast.error("Failed to update status");
      }
    },
    [updateVisitor],
  );

  const { inVisitors, actionOptions } = useVisitorSelection(
    visitors,
    totalCount,
    selectedRows,
  );

  const { handleActionSelect } = useVisitorActions({
    setSignOut,
    setDeleteRow,
  });

  const columns: any = useMemo(
    () => visitorColumns(handleStatusChange, theme, t),
    [handleStatusChange, theme, t],
  );

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div
      className={twMerge(
        "p-4 lg:p-10 space-y-4 transition-colors rounded",
        theme === "dark" ? "bg-[#191919]" : "bg-white",
      )}
    >
      <div className="flex flex-wrap items-center justify-between py-4">
        <h2
          className={twMerge(
            "text-lg md:text-3xl font-semibold",
            theme === "dark" ? "text-gray-100" : "text-[#3b3b3b]",
          )}
        >
          {t("visitors.timeline.title")} <OfficeLocation />
        </h2>
        {
          <div className="border border-green-500 text-green-500 rounded-full p-1 min-w-32 text-center font-bold">
            {inVisitors.length}{" "}
            <span className="font-normal text-sm">
              {t("visitors.timeline.inCount")}
            </span>
          </div>
        }
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <Input
            placeholder={t("visitors.timeline.search")}
            icon={<Search size={18} />}
            value={searchInput}
            onChange={setSearchInput}
          />
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(start, end) =>
              setFilters({ ...filters, startDate: start, endDate: end })
            }
          />
          <button
            className="text-[#099ed1] flex items-center gap-1 font-bold"
            onClick={() => setIsFilterOpen((p) => !p)}
          >
            <SlidersHorizontal size={14} />
            {t("visitors.timeline.filters")}
          </button>
        </div>

        <div className="flex items-center gap-8">
          <DropDown
            options={actionOptions}
            buttonLabel={t("visitors.timeline.actions")}
            onSelect={(action) =>
              handleActionSelect(action, visitors, selectedRows)
            }
          />
          <Button className="w-30" onClick={() => setAddVisit(true)}>
            {t("visitors.timeline.newVisit")}
          </Button>
        </div>
      </div>

      {/* Conditional Filter Section */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.25, ease: "easeInOut" },
              opacity: { duration: 0.15, ease: "easeInOut" },
            }}
            className="overflow-hidden mb-4"
          >
            <div
              className={twMerge(
                "border p-4 flex gap-4 items-center",
                theme === "dark"
                  ? "bg-[#262727] border-gray-700"
                  : "bg-[#f5fbfd] border-[#c4e7f5]",
              )}
            >
              <Select
                label={t("visitors.timeline.status")}
                value={filters.status}
                options={STATUS_FILTERS}
                onChange={(value) => setFilters({ ...filters, status: value })}
                className="md:w-50"
              />

              <Select
                label={t("visitors.timeline.category")}
                value={filters.category}
                options={categories}
                onChange={(value) =>
                  setFilters({ ...filters, category: value })
                }
                className="md:w-50"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <Table
        data={visitors}
        columns={columns}
        loading={loading}
        selectable={{
          active: true,
          selectedRows,
          setSelectedRows,
          rowIdKey: "id",
        }}
        setSelectedRow={(row: Visitor) => {
          if (row.anonymize) return;
          setSelectedVisit(row);
          setEditTimeline(true);
        }}
        selectedRow={selectedVisit}
        emptyObject={{
          icon: <User size={32} />,
          title: t("visitors.timeline.noVisitors"),
        }}
        setSorted={(value) =>
          setFilters((prev) => ({
            ...prev,
            sorted: value,
          }))
        }
      />

      {/* Pagination */}
      {visitors && visitors.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Modals */}

      <AddNewVisit isOpen={addVisit} onClose={() => setAddVisit(false)} />

      <EditTimeline
        isOpen={editTimeline}
        onClose={() => {
          setEditTimeline(false);
          setSelectedVisit(null);
        }}
        timeline={selectedVisit}
        allvisitors={visitors}
        setTimeline={setSelectedVisit}
      />

      <DeleteRow
        isOpen={deleteRow}
        onClose={() => {
          setDeleteRow(false);
          setSelectedRows([]);
        }}
        label="visitor"
        timeline={selectedRows.length > 0 ? selectedRows : visitors}
      />

      <SignOutModal
        isOpen={signOut}
        onClose={() => {
          setSignOut(false);
          setSelectedRows([]);
        }}
        timeline={selectedRows.length > 0 ? selectedRows : visitors}
      />
    </div>
  );
};

export default Timeline;
